import { GoogleGenAI, Type, FunctionDeclaration, Modality } from "@google/genai";
import { PrayerRequest } from "../types";

export const getGeminiFunctions = (): FunctionDeclaration[] => {
    return [
        {
            name: 'addPrayerRequest',
            description: 'Adds a new prayer request to the journal.',
            parameters: {
                type: Type.OBJECT,
                properties: {
                    title: { type: Type.STRING, description: 'A short title for the prayer request.' },
                    requestor: { type: Type.STRING, description: 'The name of the person the prayer is for. Default to "me" if not specified.' },
                    type: { type: Type.STRING, description: 'The type of prayer, e.g., Thanksgiving, Petition, Intercession, Praise, Lament.' },
                    details: { type: Type.STRING, description: 'The full details of the prayer request.' },
                    listName: { type: Type.STRING, description: 'Optional. The name of the prayer list to add this request to.' },
                },
                required: ['title', 'type', 'details', 'requestor'],
            },
        },
        {
            name: 'updatePrayerRequest',
            description: 'Updates an existing prayer request, usually to mark it as answered.',
            parameters: {
                type: Type.OBJECT,
                properties: {
                    title: { type: Type.STRING, description: 'The title of the prayer request to update.' },
                    status: { type: Type.STRING, description: 'The new status, should be "Answered".' },
                    answeredDetails: { type: Type.STRING, description: 'A description of how the prayer was answered.' },
                },
                required: ['title', 'status', 'answeredDetails'],
            },
        },
        {
            name: 'findPrayerRequests',
            description: 'Finds and lists prayer requests based on status, type, requestor, or list name.',
            parameters: {
                type: Type.OBJECT,
                properties: {
                    status: { type: Type.STRING, description: 'The status to filter by, e.g., Current, Ongoing, Answered.' },
                    type: { type: Type.STRING, description: 'The prayer type to filter by.' },
                    requestor: { type: Type.STRING, description: 'The name of the person to filter by.' },
                    listName: { type: Type.STRING, description: 'The name of the prayer list to show.' },
                },
            },
        },
        {
            name: 'startPrayerTime',
            description: 'Initiates a focused prayer session, starting with pending prayer requests from a specific list or all lists.',
             parameters: {
                type: Type.OBJECT,
                properties: {
                    listName: { type: Type.STRING, description: 'Optional. The name of the list to pray for.' },
                },
            },
        },
        {
            name: 'recommendAndReadVerse',
            description: 'Finds a relevant bible verse for a prayer request and reads it aloud using a special TTS voice.',
             parameters: {
                type: Type.OBJECT,
                properties: {
                    title: { type: Type.STRING, description: 'The title of the prayer request to find a verse for.' },
                },
                required: ['title'],
            },
        },
        {
            name: 'setFavoriteBibleVersion',
            description: 'Sets the user\'s preferred Bible version for scripture reading.',
            parameters: {
                type: Type.OBJECT,
                properties: {
                    version: { type: Type.STRING, description: 'The Bible version, e.g., "NIV", "KJV", "ESV".' },
                },
                required: ['version'],
            },
        },
        {
            name: 'createPrayerList',
            description: 'Creates a new list to organize prayer requests.',
            parameters: {
                type: Type.OBJECT,
                properties: {
                    name: { type: Type.STRING, description: 'The name for the new prayer list.' },
                },
                required: ['name'],
            },
        },
        {
            name: 'movePrayerToList',
            description: 'Moves an existing prayer request to a different list.',
            parameters: {
                type: Type.OBJECT,
                properties: {
                    prayerTitle: { type: Type.STRING, description: 'The title of the prayer to move.' },
                    listName: { type: Type.STRING, description: 'The name of the destination list.' },
                },
                required: ['prayerTitle', 'listName'],
            },
        },
        {
            name: 'renamePrayerRequest',
            description: 'Renames an existing prayer request.',
            parameters: {
                type: Type.OBJECT,
                properties: {
                    oldTitle: { type: Type.STRING, description: 'The current title of the prayer request to rename.' },
                    newTitle: { type: Type.STRING, description: 'The new title for the prayer request.' },
                },
                required: ['oldTitle', 'newTitle'],
            },
        },
        {
            name: 'renamePrayerList',
            description: 'Renames an existing prayer list.',
            parameters: {
                type: Type.OBJECT,
                properties: {
                    oldName: { type: Type.STRING, description: 'The current name of the prayer list to rename.' },
                    newName: { type: Type.STRING, description: 'The new name for the prayer list.' },
                },
                required: ['oldName', 'newName'],
            },
        },
        {
            name: 'deletePrayerRequest',
            description: 'Deletes a prayer request from the journal.',
            parameters: {
                type: Type.OBJECT,
                properties: {
                    title: { type: Type.STRING, description: 'The title of the prayer request to delete.' },
                },
                required: ['title'],
            },
        },
        {
            name: 'deletePrayerList',
            description: 'Deletes a prayer list. Prayers in the list will be moved to the "General" list.',
            parameters: {
                type: Type.OBJECT,
                properties: {
                    name: { type: Type.STRING, description: 'The name of the prayer list to delete.' },
                },
                required: ['name'],
            },
        },
    ];
};

// FIX: Updated prayer parameter type to only require properties that are used.
export const fetchBibleVerseRecommendation = async (prayer: { title: string, details: string }): Promise<string> => {
    try {
        const ai = new GoogleGenAI({ apiKey: process.env.API_KEY! });
        const prompt = `Based on the following prayer request, recommend a single, relevant, and encouraging Bible verse. Prayer Title: "${prayer.title}". Details: "${prayer.details}". Please respond with ONLY the verse reference and text, like 'John 3:16 - "For God so loved the world..."'.`;

        const response = await ai.models.generateContent({
            model: 'gemini-2.5-flash',
            contents: [{ parts: [{ text: prompt }] }],
        });

        return response.text.trim();
    } catch (error) {
        console.error("Error fetching Bible verse:", error);
        return "Could not fetch a recommendation at this time.";
    }
};

export const fetchVerseAudio = async (verse: string, version: string): Promise<string | null> => {
    try {
        const ai = new GoogleGenAI({ apiKey: process.env.API_KEY! });

        const textResponse = await ai.models.generateContent({
            model: 'gemini-2.5-flash',
            contents: [{ parts: [{ text: `What is the full text of ${verse} in the ${version} translation?` }] }],
        });
        const verseText = textResponse.text.trim();
        if (!verseText) throw new Error("Could not get verse text.");

        const ttsResponse = await ai.models.generateContent({
            model: "gemini-2.5-flash-preview-tts",
            contents: [{ parts: [{ text: `Say with a calm and clear voice: ${verseText}` }] }],
            config: {
                responseModalities: [Modality.AUDIO],
                speechConfig: {
                    voiceConfig: {
                        prebuiltVoiceConfig: { voiceName: 'Kore' },
                    },
                },
            },
        });

        const base64Audio = ttsResponse.candidates?.[0]?.content?.parts?.[0]?.inlineData?.data;
        return base64Audio || null;
    } catch (error) {
        console.error("Error fetching verse audio:", error);
        return null;
    }
};
